import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Divider,
} from '@mui/material';
import PostData from '../../services/PostData.js';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

const CANCEL_OPTIONS = {
    walmart: [
        { value: 'CUSTOMER_REQUESTED_SELLER_TO_CANCEL', label: 'Customer Requested' },
        { value: 'SELLER_CANCEL_PRICING_ERROR', label: 'Pricing Error' },
        { value: 'SELLER_CANCEL_OUT_OF_STOCK', label: 'Out of Stock' },
        { value: 'SELLER_CANCEL_FRAUD_STOP_SHIPMENT', label: 'Fraud' },
        { value: 'SELLER_CANCEL_ADDRESS_NOT_SERVICEABLE', label: 'Address not serviceable' },
    ],
    ebay: [
        { value: 'BUYER_ASKED_CANCEL', label: 'Buyer Request' },
        { value: 'UNKNOWN', label: 'Unknown' },
        { value: 'ADDRESS_ISSUES', label: 'Shipping Address Issue' },
        { value: 'BUYER_CANCEL_OR_ADDRESS_ISSUE', label: 'Buyer cancel / Address issue' },
        { value: 'FOUND_CHEAPER_PRICE', label: 'Cheaper price elsewhere' },
        { value: 'ORDER_MISTAKE', label: 'Order mistake' },
        { value: 'ORDER_UNPAID', label: 'Unpaid order' },
        { value: 'OUT_OF_STOCK_OR_CANNOT_FULFILL', label: 'Out of stock / Cant fulfill' },
        { value: 'WONT_ARRIVE_IN_TIME', label: 'Wont arrive in time' },
        { value: 'WRONG_PAYMENT_METHOD', label: 'Wrong Payment Method' },
        { value: 'WRONG_SHIPPING_ADDRESS', label: 'Wrong Shipping Address' },
        { value: 'WRONG_SHIPPING_METHOD', label: 'Wrong Shipping Method' },
    ],
    default: [
        { value: 'CUSTOMER_REQUESTED', label: 'Customer Requested' },
        { value: 'UNAVAILABLE', label: 'Item Unavailable' },
        { value: 'OTHER', label: 'Other' },
    ],
};

export default function CancelOrderDialog({
    isOpen,
    onClose,
    onSuccess,
    orderChannel,
    orderId,
}) {
    const [submitLoad, setSubmitLoad] = useState(false);
    const [successSubmit, setSuccessSubmit] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedReason, setSelectedReason] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedReason('');
            setSuccessSubmit(false);
            setErrorMessage('');
            setSubmitLoad(false);
        }
    }, [isOpen]);

    const handleConfirm = async () => {
        if (!selectedReason || !orderId?.customerOrderNumber) return;
        setSubmitLoad(true);
        setErrorMessage('');
        const payload = {
            channel: orderId.orderChannel || orderChannel,
            orderNum: orderId.customerOrderNumber,
            reason: selectedReason,
            quantity: orderId.quantity,
            recId: orderId.id,
        };
        const { success, error } = await PostData(
            payload,
            'broylesllc/order/cancel-order',
            null
        );
        setSubmitLoad(false);
        if (success) {
            setSuccessSubmit(true);
            setTimeout(() => {
                if (onSuccess) onSuccess();
                else onClose?.();
            }, 800);
            return;
        }
        const message =
            error?.response?.data?.message ||
            (typeof error?.response?.data === 'string' ? error.response.data : '') ||
            error?.message ||
            'Could not cancel this order.';
        setErrorMessage(String(message));
    };

    const channelKey = String(orderId?.orderChannel || orderChannel || '').toLowerCase();
    const options = CANCEL_OPTIONS[channelKey] || CANCEL_OPTIONS.default;

    return (
        <Dialog open={isOpen} onClose={submitLoad ? undefined : onClose}>
            <DialogTitle sx={{ padding: '10px 15px 5px 15px' }}>
                Confirm Order Cancellation
            </DialogTitle>
            <Divider sx={{ borderColor: 'black' }} />
            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    You are about to cancel order:{' '}
                    <strong>{orderId?.customerOrderNumber}</strong> from channel:{' '}
                    <strong>{orderId?.orderChannel || orderChannel}</strong>.
                </Typography>
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel size="small" id="cancellation-reason-label">
                        Cancellation Reason
                    </InputLabel>
                    <Select
                        size="small"
                        labelId="cancellation-reason-label"
                        id="cancellation-reason-select"
                        value={selectedReason}
                        label="Cancellation Reason"
                        onChange={(event) => setSelectedReason(event.target.value)}
                        disabled={submitLoad || successSubmit}
                    >
                        {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {errorMessage ? (
                    <Typography color="error" sx={{ mt: 2, fontSize: 13 }}>
                        {errorMessage}
                    </Typography>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button onClick={onClose} color="primary" disabled={submitLoad}>
                    Close
                </Button>
                <Button
                    loading={submitLoad}
                    disabled={submitLoad || !selectedReason || successSubmit}
                    onClick={handleConfirm}
                    color="error"
                >
                    {successSubmit ? (
                        <>
                            <span>Success</span>
                            <CheckOutlinedIcon
                                fontSize="small"
                                sx={{ color: 'white', marginLeft: '10px' }}
                            />
                        </>
                    ) : (
                        'Cancel order'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
